import { useEffect, useState, useCallback } from 'react';
import { Typography, Box, CircularProgress, Alert, ToggleButton, ToggleButtonGroup } from '@mui/material';
import DateRangeSelector from '../../components/DateRangeSelector';
import { PAGE_CARD_SX } from '../../theme/pageCardSx';
import NaverSpentStatsTable from '../../components/naver/table/NaverSpentStatsTable';
import PublisherFilter from '../../components/naver/PublisherFilter';
import apiClient from '../../utils/axiosClient';
import dayjs, { Dayjs } from 'dayjs';
import { NaverHourlySpentStats, NaverDailySpentStats } from '../../types/naver.types';

const REFRESH_INTERVAL_MS = 15 * 60 * 1000;
const REFRESH_OFFSET_MS = 1 * 60 * 1000;

const GRANULARITY_ENDPOINT = {
  hourly: '/api/v1/stat/naver/hourly/spent',
  daily: '/api/v1/stat/naver/daily/spent',
} as const;

type Granularity = keyof typeof GRANULARITY_ENDPOINT;

// 매시 1분, 16분, 31분, 46분처럼 15분 주기 + 1분 오프셋에 정렬된 다음 새로고침까지 남은 ms
const msUntilNextRefresh = () => {
  const now = Date.now();
  const next = Math.ceil((now - REFRESH_OFFSET_MS) / REFRESH_INTERVAL_MS) * REFRESH_INTERVAL_MS + REFRESH_OFFSET_MS;
  return next - now;
};

export default function NaverSpentStatsPage() {
  const [granularity, setGranularity] = useState<Granularity>('hourly');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [selectedPublisher, setSelectedPublisher] = useState<string | undefined>(undefined);
  const [data, setData] = useState<(NaverHourlySpentStats | NaverDailySpentStats)[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const fetchData = useCallback(() => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setErrorMessage('');
    apiClient
      .get(GRANULARITY_ENDPOINT[granularity], {
        params: {
          start_date: startDate.format('YYYY-MM-DD'),
          end_date: endDate.format('YYYY-MM-DD'),
          ...(selectedPublisher ? { publisher: selectedPublisher } : {}),
        },
      })
      .then((res) => setData(res.data))
      .catch(() => setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.'))
      .finally(() => setLoading(false));
  }, [granularity, startDate, endDate, selectedPublisher]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const scheduleNext = () => {
      timeoutId = setTimeout(() => {
        fetchData();
        scheduleNext();
      }, msUntilNextRefresh());
    };

    scheduleNext();
    return () => clearTimeout(timeoutId);
  }, [fetchData]);

  return (
    <Box sx={{ opacity: loading ? 0.4 : 1, transition: 'opacity 0.3s ease', minHeight: '100vh' }}>
      <Box display="flex" alignItems="center">
        <DateRangeSelector
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onSearch={fetchData}
        />
      </Box>

      <Box
        sx={PAGE_CARD_SX}
      >
        <Alert severity="info" variant="outlined" sx={{ mb: 2, py: 0.5 }}>
          데이터는 <b>15분 주기</b>로 자동 갱신됩니다.
        </Alert>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          <PublisherFilter selected={selectedPublisher} onSelect={setSelectedPublisher} />
          <ToggleButtonGroup
            value={granularity}
            exclusive
            size="small"
            onChange={(_, value: Granularity | null) => value && setGranularity(value)}
            sx={{
              width: 'fit-content',
              '& .MuiToggleButton-root': {
                px: 2.5,
                py: 0.5,
                fontSize: '0.85rem',
                fontWeight: 500,
                color: '#64748b',
                borderColor: '#e2e8f0',
                textTransform: 'none',
                '&.Mui-selected': {
                  backgroundColor: '#0044D2',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#0033a0' },
                },
              },
            }}
          >
            <ToggleButton value="hourly">시간별</ToggleButton>
            <ToggleButton value="daily">일별</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              데이터 로딩 중...
            </Typography>
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : data && startDate && endDate && (
          <NaverSpentStatsTable data={data} startDate={startDate} endDate={endDate} granularity={granularity} />
        )}
      </Box>
    </Box>
  );
}
